import { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload, Image, Zap, CheckCircle, AlertTriangle,
  Edit3, Trash2, ChevronRight, ArrowLeft, Plus, X, Info,
  Mail, FileSpreadsheet, Users, UserCheck, UserX, Phone,
  ChevronDown, RefreshCw,
} from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import toast from 'react-hot-toast';

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  s1: 'rgba(255,255,255,0.04)', s2: 'rgba(255,255,255,0.07)', s3: 'rgba(255,255,255,0.10)',
  border: 'var(--border)', text: 'var(--text)', text2: 'var(--text-2)',
  ice: 'var(--ice)', iceDim: 'var(--ice-a8)', iceBdr: 'var(--ice-a20)',
  goal: '#00e87a', penalty: '#ff3b3b', puck: '#ff8c1a',
};

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const MAX_IMAGES  = 100;
const STEPS       = ['Upload', 'Analyzing', 'Review', 'Done'];

// ── Classification system ─────────────────────────────────────────────────────
export const CLASSES = {
  player_full:  { key: 'player_full',  label: 'Player – Full Time', short: 'Player FT', emoji: '👤', isSpare: false, position: 'flex',   color: '#00d4ff' },
  player_spare: { key: 'player_spare', label: 'Player Spare',        short: 'Spare',     emoji: '👥', isSpare: true,  position: 'flex',   color: '#ff9500' },
  goalie_full:  { key: 'goalie_full',  label: 'Goalie – Full Time',  short: 'Goalie FT', emoji: '🥅', isSpare: false, position: 'goalie', color: '#00e87a' },
  goalie_spare: { key: 'goalie_spare', label: 'Goalie Spare',         short: 'G Spare',   emoji: '🥅', isSpare: true,  position: 'goalie', color: '#7dd3b8' },
};
const CLASSES_LIST = Object.values(CLASSES);

function ClassBadge({ cls, size = 'sm', onClick }) {
  const c = CLASSES[cls];
  if (!c) return null;
  return (
    <span onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'sm' ? '2px 8px' : '5px 12px',
      borderRadius: 20, fontSize: size === 'sm' ? 11 : 13,
      fontWeight: 700, cursor: onClick ? 'pointer' : 'default',
      background: `${c.color}18`, border: `1px solid ${c.color}40`,
      color: c.color, whiteSpace: 'nowrap', letterSpacing: '0.02em',
    }}>
      {c.emoji} {size === 'sm' ? c.short : c.label}
      {onClick && <ChevronDown size={10} />}
    </span>
  );
}

function ClassPicker({ value, onChange, compact = false }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
      {CLASSES_LIST.map(cls => (
        <button key={cls.key} onClick={() => onChange(cls.key)} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: compact ? '6px 10px' : '9px 12px', borderRadius: 10, textAlign: 'left',
          background: value === cls.key ? `${cls.color}18` : T.s2,
          border: `1.5px solid ${value === cls.key ? cls.color + '60' : 'var(--border)'}`,
          color: value === cls.key ? cls.color : 'var(--text-2)',
          cursor: 'pointer', transition: 'all 0.15s',
        }}>
          <span style={{ fontSize: 16 }}>{cls.emoji}</span>
          <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>{cls.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── File helpers ──────────────────────────────────────────────────────────────
const readAsDataURL = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = () => rej(new Error('Read failed')); r.readAsDataURL(f); });
const readAsText   = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = () => rej(new Error('Read failed')); r.readAsText(f); });
const readAsArrBuf = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = () => rej(new Error('Read failed')); r.readAsArrayBuffer(f); });
const fmtSize = b => b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  const delim = lines[0].includes('\t') ? '\t' : ',';
  const parseRow = line => {
    const cols = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === delim && !inQ) { cols.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    cols.push(cur.trim()); return cols;
  };
  return rowsToPlayers(lines.map(parseRow));
}

// Shared by the CSV and Excel paths: rows of trimmed strings in, players out.
function rowsToPlayers(rows) {
  if (!rows.length) return [];
  const EML = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHN = /^[\d\s\-().+]{7,20}$/;
  const hdr = rows[0].map(h => h.toLowerCase());
  const eI = hdr.findIndex(h => h.includes('email') || h.includes('e-mail'));
  const nI = hdr.findIndex(h => h.includes('name') || h.includes('player') || h.includes('member'));
  const pI = hdr.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('cell'));
  const start = (eI >= 0 || nI >= 0 || pI >= 0) ? 1 : 0;
  const result = [];
  for (let i = start; i < rows.length; i++) {
    const row = rows[i];
    if (row.every(c => !c)) continue;
    let name  = nI >= 0 && row[nI] ? row[nI] : null;
    let email = eI >= 0 && row[eI] && EML.test(row[eI]) ? row[eI].toLowerCase() : null;
    let phone = pI >= 0 && row[pI] && PHN.test(row[pI]) ? row[pI] : null;
    if (!email) { for (const c of row) { if (c && EML.test(c)) { email = c.toLowerCase(); break; } } }
    if (!phone) { for (const c of row) { if (c && PHN.test(c) && !EML.test(c)) { phone = c; break; } } }
    if (!name)  { for (const c of row) { if (c && !EML.test(c) && !PHN.test(c)) { name = c; break; } } }
    if (name || email || phone) result.push({ name: name||null, email: email||null, phone: phone||null });
  }
  return result;
}

// 2026-10-01 - .xlsx is read with read-excel-file (maintained) instead of
// SheetJS 0.18.5, the last SheetJS on npm, which has unpatched prototype-
// pollution and ReDoS advisories for crafted files. The "universal" build
// reads an ArrayBuffer on the main thread (no Web Worker); a roster is small.
// Legacy .xls (Excel 97-2003) is not supported by it, so that format gets a
// clear "save as .xlsx or CSV" message instead.
const cellText = v => v == null ? '' : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim();
async function parseExcel(buf, name = '') {
  if (/\.xls$/i.test(name)) throw new Error('Old Excel (.xls) files are not supported. In Excel, use File > Save As > .xlsx (or CSV) and upload that.');
  let readSheet;
  try { ({ readSheet } = await import('read-excel-file/universal')); }
  catch { throw new Error('Could not load the Excel reader. Export the sheet as CSV instead.'); }
  let data;
  try { data = await readSheet(buf); }
  catch { throw new Error('This file could not be read as an Excel workbook. Save it as .xlsx or CSV and try again.'); }
  const rows = (data || []).map(r => r.map(cellText)).filter(r => r.some(c => c));
  return rowsToPlayers(rows);
}

// ── Step bar ──────────────────────────────────────────────────────────────────
function StepBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 40 }}>
      {STEPS.map((label, i) => {
        const active = i === step, done = i < step;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif",
                background: done ? T.goal : active ? T.ice : T.s2,
                color: done || active ? '#000' : T.text2,
                border: `2px solid ${done ? T.goal : active ? T.ice : T.border}`,
                transition: 'all 0.3s', boxShadow: active ? `0 0 16px ${T.iceBdr}` : 'none',
              }}>{done ? '✓' : i + 1}</div>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: active ? T.ice : done ? T.goal : T.text2, whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, margin: '-18px 8px 0', background: done ? T.goal : T.border, transition: 'background 0.3s' }} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Upload ────────────────────────────────────────────────────────────
function UploadStep({ imageQueue, preset, onPresetChange, onAddImages, onRemoveImage, onSpreadsheet }) {
  const [dragging, setDragging] = useState(false);
  const imgRef = useRef(null);
  const csvRef = useRef(null);

  const processFiles = useCallback(async fileList => {
    const files  = Array.from(fileList);
    const images = files.filter(f => IMAGE_TYPES.has(f.type) || /\.(jpg|jpeg|png|webp|gif)$/i.test(f.name));
    const csv    = files.find(f => f.type === 'text/csv' || f.name.endsWith('.csv') || f.type === 'text/plain');
    const xlsx   = files.find(f => f.type.includes('sheet') || f.type.includes('excel') || /\.(xlsx|xls)$/i.test(f.name));

    if ((csv || xlsx) && !images.length) {
      const file = csv || xlsx;
      if (file.size > 14 * 1048576) { toast.error('File too large (14 MB max).'); return; }
      try {
        const players = csv ? parseCSV(await readAsText(file)) : await parseExcel(await readAsArrBuf(file), file.name);
        onSpreadsheet({ players, fileInfo: { name: file.name, mode: csv ? 'csv' : 'excel' } });
      } catch (err) { toast.error(err.message); }
      return;
    }
    if (!images.length) { toast.error('Unsupported file. Upload screenshots (JPG/PNG) or a spreadsheet (CSV/XLSX).'); return; }
    const big = images.filter(f => f.size > 14 * 1048576);
    if (big.length) toast.error(`${big.length} file(s) exceed 14 MB and were skipped.`);
    const valid = images.filter(f => f.size <= 14 * 1048576);
    if (!valid.length) return;
    const entries = await Promise.all(valid.map(async f => {
      const dataUrl = await readAsDataURL(f);
      return { id: Math.random(), name: f.name, mimeType: f.type || 'image/jpeg', data: dataUrl.split(',')[1], size: f.size, preview: dataUrl, status: 'pending', count: 0, error: null };
    }));
    onAddImages(entries);
  }, [onAddImages, onSpreadsheet]);

  const onDrop = e => { e.preventDefault(); setDragging(false); processFiles(e.dataTransfer.files); };
  const isEmpty = imageQueue.length === 0;

  return (
    <div>
      {/* ── Classification preset ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>Default classification for all imported players</p>
          {preset && <ClassBadge cls={preset} />}
        </div>
        <ClassPicker value={preset} onChange={onPresetChange} />
        {preset && (
          <button onClick={() => onPresetChange(null)} style={{ marginTop: 8, fontSize: 11, color: T.text2, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            Clear preset (set per player in review)
          </button>
        )}
      </div>

      {/* ── Drop zone ── */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => isEmpty && imgRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? T.ice : T.border}`, borderRadius: 16,
          padding: isEmpty ? '36px 32px' : '16px 24px', textAlign: 'center',
          cursor: isEmpty ? 'pointer' : 'default',
          background: dragging ? T.iceDim : T.s1,
          transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
        }}
      >
        <input ref={imgRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif" style={{ display: 'none' }} onChange={e => processFiles(e.target.files)} />
        <input ref={csvRef} type="file" accept=".csv,.xlsx,.xls,.txt" style={{ display: 'none' }} onChange={e => processFiles(e.target.files)} />
        {dragging && <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, var(--ice-a7) 0%, transparent 70%)', pointerEvents: 'none' }} />}

        {isEmpty ? (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ width: 64, height: 64, borderRadius: 14, background: T.iceDim, border: `1px solid ${T.iceBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Upload size={28} style={{ color: T.ice }} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Drop contact screenshots here</p>
            <p style={{ color: T.text2, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>Up to {MAX_IMAGES} screenshots · or one CSV / Excel file</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[{ i: <Image size={13} />, l: `Screenshots (×${MAX_IMAGES})` }, { i: <FileSpreadsheet size={13} />, l: 'CSV' }, { i: <FileSpreadsheet size={13} />, l: 'Excel' }].map(({ i, l }) => (
                <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: T.s2, border: `1px solid ${T.border}`, fontSize: 11, color: T.text2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{i} {l}</span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', position: 'relative', zIndex: 1 }}>
            <Upload size={14} style={{ color: T.ice }} />
            <span style={{ fontSize: 13, color: T.ice, fontWeight: 600 }}>Drop more here</span>
            <span style={{ fontSize: 12, color: T.text2 }}>({imageQueue.length}/{MAX_IMAGES})</span>
          </div>
        )}
      </div>

      {/* ── Queue list ── */}
      {imageQueue.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{imageQueue.length} screenshot{imageQueue.length !== 1 ? 's' : ''} queued</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => imgRef.current?.click()} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 7, background: T.s2, border: `1px solid ${T.border}`, color: T.ice, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}><Plus size={11} /> Add more</button>
              <button onClick={() => csvRef.current?.click()} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 7, background: T.s2, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}><FileSpreadsheet size={11} /> Use CSV</button>
            </div>
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', borderRadius: 12, border: `1px solid ${T.border}` }}>
            {imageQueue.map((f, idx) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', borderBottom: idx < imageQueue.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <img src={f.preview} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 5, flexShrink: 0, border: `1px solid ${T.border}` }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 1 }}>{f.name}</p>
                  <p style={{ fontSize: 11, color: T.text2 }}>{fmtSize(f.size)}</p>
                </div>
                <button onClick={() => onRemoveImage(f.id)} style={{ background: 'none', border: 'none', color: T.text2, cursor: 'pointer', padding: 3, display: 'flex' }}><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, padding: '12px 16px', background: T.s1, borderRadius: 10, border: `1px solid ${T.border}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Info size={15} style={{ color: T.ice, flexShrink: 0, marginTop: 1 }} />
        <div style={{ color: T.text2, fontSize: 13, lineHeight: 1.6 }}>
          <strong style={{ color: T.text }}>Screenshots</strong> — AI extracts name, email &amp; phone. Upload up to {MAX_IMAGES}.<br />
          <strong style={{ color: T.text }}>CSV / Excel</strong> — Name, Email, Phone columns auto-detected.
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Analyzing ─────────────────────────────────────────────────────────
function AnalyzingStep({ imageQueue }) {
  const done = imageQueue.filter(f => f.status === 'done').length;
  const errs = imageQueue.filter(f => f.status === 'error').length;
  const pct  = Math.round((done + errs) / imageQueue.length * 100);
  const cur  = imageQueue.find(f => f.status === 'analyzing');
  return (
    <div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes ping{0%{transform:scale(1);opacity:.6}100%{transform:scale(1.6);opacity:0}}`}</style>
      <div style={{ textAlign: 'center', padding: '24px 0 18px' }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 18 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: T.iceDim, border: `2px solid ${T.iceBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'spin 2s linear infinite' }}><Users size={26} style={{ color: T.ice }} /></div>
          <div style={{ position: 'absolute', inset: -7, borderRadius: '50%', border: `2px solid ${T.iceBdr}`, animation: 'ping 1.5s ease-out infinite', opacity: 0.6 }} />
        </div>
        <h2 style={{ fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.text, marginBottom: 5 }}>Scanning Screenshots</h2>
        <p style={{ color: T.text2, fontSize: 13 }}>{cur ? <>Reading <strong style={{ color: T.text }}>{cur.name}</strong>…</> : 'Finishing up…'}</p>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: T.text2 }}>{done + errs} of {imageQueue.length} processed</span>
          <span style={{ fontSize: 12, color: T.ice, fontWeight: 700 }}>{pct}%</span>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: T.s2, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: T.ice, transition: 'width 0.4s ease' }} />
        </div>
      </div>
      <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, maxHeight: 340, overflowY: 'auto' }}>
        {imageQueue.map((f, idx) => (
          <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 13px', borderBottom: idx < imageQueue.length - 1 ? `1px solid ${T.border}` : 'none' }}>
            <img src={f.preview} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0, border: `1px solid ${T.border}` }} />
            <span style={{ flex: 1, fontSize: 12, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <span style={{ fontSize: 12, fontWeight: 700, flexShrink: 0, color: f.status === 'done' ? T.goal : f.status === 'error' ? T.penalty : f.status === 'analyzing' ? T.ice : T.text2 }}>
              {f.status === 'done' ? `✓ ${f.count}` : f.status === 'error' ? '✗' : f.status === 'analyzing' ? '⚡' : '⏳'}
            </span>
          </div>
        ))}
      </div>
      {errs > 0 && <p style={{ fontSize: 12, color: T.penalty, marginTop: 8, textAlign: 'center' }}>{errs} screenshot{errs !== 1 ? 's' : ''} failed — results from the rest will still be shown.</p>}
    </div>
  );
}

// ── Step 3: Review ────────────────────────────────────────────────────────────
const EML_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ReviewStep({ players: init, provider, filename, preset, onConfirm, onReset }) {
  const [players,     setPlayers]     = useState(() => init.map((p, i) => ({
    ...p, phone: p.phone || '',
    classification: preset || 'player_full',
    _id: i, _selected: true,
  })));
  const [editing,     setEditing]     = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [bulkOpen,    setBulkOpen]    = useState(false);
  // 2026-07-10 (Phase 3.3, coach request) — press-and-drag multi-select:
  // hold the mouse/touchpad button down on a checkbox and sweep across
  // rows; every row you pass flips to the SAME state as the first one
  // (check or uncheck), so bulk selection doesn't need a click per row.
  // Stable mutable holder via useState initializer — no re-render needed
  // for the drag flag itself, and no extra hook imports.
  const [dragSel] = useState({ target: null });

  const provLabel  = provider ? 'AI Analysis' : 'Spreadsheet';
  const selected   = players.filter(p => p._selected);
  const emailIssue = selected.some(p => p.email && !EML_RE.test(p.email)); // invalid email (not missing)

  const update    = (id, patch) => setPlayers(ps => ps.map(p => p._id === id ? { ...p, ...patch } : p));
  const remove    = (id)        => setPlayers(ps => ps.filter(p => p._id !== id));
  const toggleAll = () => { const all = players.every(p => p._selected); setPlayers(ps => ps.map(p => ({ ...p, _selected: !all }))); };

  const bulkReclassify = (cls) => {
    setPlayers(ps => ps.map(p => p._selected ? { ...p, classification: cls } : p));
    setBulkOpen(false);
    toast.success(`Reclassified ${selected.length} player${selected.length !== 1 ? 's' : ''} to ${CLASSES[cls]?.label}`);
  };

  const addRow = () => {
    const row = { _id: Date.now(), _selected: true, name: '', email: '', phone: '', classification: preset || 'player_full' };
    setPlayers(ps => [...ps, row]); setEditing(row._id);
  };

  const handleConfirm = async () => {
    if (!selected.length)  { toast.error('Select at least one player'); return; }
    if (emailIssue)        { toast.error('Some selected players are missing a valid email'); return; }
    setSubmitting(true);
    try {
      await onConfirm(selected.map(({ name, email, phone, classification }) =>
        ({ name, email, phone: phone || null, classification })
      ));
    } finally { setSubmitting(false); }
  };

  const inp = { background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, padding: '6px 10px', width: '100%', outline: 'none', fontFamily: 'inherit' };

  return (
    <div>
      {/* Summary */}
      <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.15)', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <CheckCircle size={16} style={{ color: T.goal, flexShrink: 0 }} />
        <p style={{ color: T.text, fontSize: 14, margin: 0, flex: 1 }}>
          <strong style={{ color: T.goal }}>{provLabel}</strong> found <strong>{players.length}</strong> player{players.length !== 1 ? 's' : ''} in <em style={{ color: T.text2 }}>{filename}</em>.
        </p>
        <span style={{ fontSize: 13, color: T.text2 }}><strong style={{ color: T.ice }}>{selected.length}</strong> of {players.length} selected</span>
      </div>

      {/* Bulk actions bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: T.text2, fontWeight: 600 }}>{selected.length} selected</span>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setBulkOpen(v => !v)}
            disabled={!selected.length}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 8, background: selected.length ? T.s2 : T.s1, border: `1px solid ${selected.length ? T.border : 'transparent'}`, color: selected.length ? T.ice : T.text2, cursor: selected.length ? 'pointer' : 'default' }}
          >
            <RefreshCw size={12} /> Reclassify selected <ChevronDown size={11} />
          </button>
          {bulkOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 4, background: 'var(--surface)', border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', minWidth: 240 }}>
              <p style={{ fontSize: 11, color: T.text2, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Apply to {selected.length} selected</p>
              <ClassPicker value={null} onChange={bulkReclassify} compact />
              <button onClick={() => setBulkOpen(false)} style={{ width: '100%', marginTop: 8, padding: '5px 0', background: 'none', border: `1px solid ${T.border}`, borderRadius: 7, color: T.text2, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mail size={12} style={{ color: T.ice }} />
          <span style={{ fontSize: 12, color: T.text2 }}>Toggle invite per row</span>
        </div>
      </div>

      {/* Table
          2026-07-14 (mobile pass) — 36+130+72=238px of fixed columns plus two
          1fr columns (name/email) means this clips at 390px viewports.
          overflow-x:auto wrapper + min-width lets it scroll instead of
          cutting off the classification/actions columns. Desktop already
          had room so this wrapper is a no-op there. */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 16 }}>
      <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, minWidth: 520 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 130px 72px', background: T.s2, borderBottom: `1px solid ${T.border}`, padding: '9px 14px' }}>
          <div><input type="checkbox" checked={players.length > 0 && players.every(p => p._selected)} onChange={toggleAll} style={{ cursor: 'pointer', accentColor: T.ice }} /></div>
          {['Name / Phone', 'Email', 'Classification', 'Actions'].map((h, i) => (
            <div key={i} style={{ fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>

        <div style={{ maxHeight: 480, overflowY: 'auto' }}
          onMouseUp={() => { dragSel.target = null; }}
          onMouseLeave={() => { dragSel.target = null; }}>
          {players.map(player => {
            const isEd = editing === player._id;
            const emailOk = player.email && EML_RE.test(player.email);
            return (
              <div key={player._id}
                onMouseEnter={() => {
                  // Sweep application: while the button is held, entering a
                  // row applies the drag's check/uncheck state to it.
                  if (dragSel.target !== null && player._selected !== dragSel.target) {
                    update(player._id, { _selected: dragSel.target });
                  }
                }}
                style={{ borderBottom: `1px solid ${T.border}`, background: !player._selected ? 'rgba(255,255,255,0.01)' : (!emailOk ? 'rgba(255,59,59,0.04)' : 'transparent') }}>
                <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 130px 72px', padding: '10px 14px', alignItems: 'center', opacity: player._selected ? 1 : 0.4 }}>
                  {/* readOnly + onMouseDown: the mousedown starts (and applies)
                      the drag state; onClick is suppressed so the native
                      checkbox toggle can't double-flip the anchor row. */}
                  <input type="checkbox" checked={player._selected} readOnly
                    onClick={e => e.preventDefault()}
                    onMouseDown={() => {
                      dragSel.target = !player._selected;
                      update(player._id, { _selected: dragSel.target });
                    }}
                    style={{ cursor: 'pointer', accentColor: T.ice }} />

                  {/* Name + phone */}
                  {isEd ? <input style={{ ...inp, marginRight: 6 }} value={player.name || ''} placeholder="Full name" onChange={e => update(player._id, { name: e.target.value })} />
                  : <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: T.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player.name || <span style={{ color: T.text2, fontStyle: 'italic' }}>No name</span>}</div>
                      {player.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: T.text2, marginTop: 1 }}><Phone size={9} />{player.phone}</div>}
                    </div>}

                  {/* Email */}
                  {isEd ? <input style={{ ...inp, marginRight: 6, borderColor: emailOk ? T.border : T.penalty }} type="email" value={player.email || ''} placeholder="email@example.com" onChange={e => update(player._id, { email: e.target.value.toLowerCase().trim() })} />
                  : <span style={{ fontSize: 12, color: emailOk ? T.text2 : T.penalty, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player.email || <span style={{ fontStyle: 'italic' }}>⚠ Missing</span>}</span>}

                  {/* Classification */}
                  {isEd
                    ? <select style={{ ...inp, marginRight: 6, fontSize: 12 }} value={player.classification} onChange={e => update(player._id, { classification: e.target.value })}>
                        {CLASSES_LIST.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
                      </select>
                    : <ClassBadge cls={player.classification} onClick={() => setEditing(player._id)} />}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 3 }}>
                    <button onClick={() => setEditing(isEd ? null : player._id)} style={{ background: isEd ? T.iceDim : T.s2, border: `1px solid ${isEd ? T.iceBdr : T.border}`, borderRadius: 6, color: isEd ? T.ice : T.text2, cursor: 'pointer', padding: '4px 6px', display: 'flex' }}>{isEd ? <X size={12} /> : <Edit3 size={12} />}</button>
                    <button onClick={() => remove(player._id)} style={{ background: T.s2, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text2, cursor: 'pointer', padding: '4px 6px', display: 'flex' }}><Trash2 size={12} /></button>
                  </div>
                </div>

                {/* Edit panel */}
                {isEd && (
                  <div style={{ padding: '10px 14px 12px', borderTop: `1px solid ${T.border}`, background: T.iceDim }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ fontSize: 11, color: T.text2, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</label>
                        <input style={inp} type="tel" value={player.phone || ''} placeholder="(519) 998-6070" onChange={e => update(player._id, { phone: e.target.value })} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: T.text2, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Classification</label>
                        <select style={inp} value={player.classification} onChange={e => update(player._id, { classification: e.target.value })}>
                          {CLASSES_LIST.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button onClick={() => setEditing(null)} style={{ background: T.ice, border: 'none', borderRadius: 7, color: '#000', cursor: 'pointer', padding: '5px 16px', fontWeight: 700, fontSize: 12 }}>Done</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {emailIssue && (
        <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12, background: 'rgba(255,140,26,0.08)', border: '1px solid rgba(255,140,26,0.25)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertTriangle size={14} style={{ color: T.puck, flexShrink: 0 }} />
          <p style={{ color: T.text, fontSize: 13, margin: 0 }}>Some players have an invalid email format. Fix or clear the email field to continue.</p>
        </div>
      )}

      {selected.some(p => !p.email) && !emailIssue && (
        <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12, background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Info size={14} style={{ color: T.ice, flexShrink: 0 }} />
          <p style={{ color: T.text, fontSize: 13, margin: 0 }}>{selected.filter(p => !p.email).length} player{selected.filter(p => !p.email).length !== 1 ? 's' : ''} have no email — they'll be saved as name-only and you can add emails later on the Players page.</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={addRow} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600, background: T.s2, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer' }}><Plus size={13} /> Add player</button>
        <button onClick={onReset} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600, background: T.s1, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer' }}>↩ Start over</button>
        <button onClick={handleConfirm} disabled={submitting || !selected.length || emailIssue} style={{
          marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 24px', borderRadius: 12, fontSize: 15, fontWeight: 700,
          background: submitting || !selected.length || emailIssue ? T.s2 : T.ice,
          color: submitting || !selected.length || emailIssue ? T.text2 : '#000',
          border: 'none', cursor: submitting ? 'wait' : 'pointer',
          fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase',
          boxShadow: selected.length && !emailIssue ? `0 4px 20px ${T.iceBdr}` : 'none', transition: 'all 0.2s',
        }}>
          {submitting ? <><div style={{ width: 15, height: 15, border: '2px solid rgba(0,0,0,0.3)', borderTopColor: '#000', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Importing…</> : <><ChevronRight size={15} /> Import {selected.length} Player{selected.length !== 1 ? 's' : ''}</>}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Done + Post-import Reclassify ────────────────────────────────────
function DoneStep({ result, teamId, onGoToRoster, onImportMore }) {
  const { saved = 0, added = 0, skipped = 0, results = [], pendingInviteIds = [] } = result;

  // Post-import reclassify state — for added (direct) and saved (pending invite) players
  const reclassifiable = results.filter(r => r.status === 'added' || r.status === 'saved');
  const [reclassify, setReclassify] = useState(() =>
    Object.fromEntries(reclassifiable.map(r => [r.email, { cls: r.classification || 'player_full', changed: false }]))
  );
  const [saving,         setSaving]         = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [invitesSent,    setInvitesSent]    = useState(false);

  const changes = Object.entries(reclassify).filter(([, v]) => v.changed);

  const saveReclassify = async () => {
    setSaving(true);
    let ok = 0, fail = 0;
    for (const r of reclassifiable) {
      const entry = reclassify[r.email];
      if (!entry?.changed) continue;
      try {
        if (r.status === 'added' && r.memberId) {
          const cls = CLASSES[entry.cls];
          await api.put(`/teams/${teamId}/members/${r.memberId}`, { position: cls.position, isSpare: cls.isSpare });
          ok++;
        } else if (r.status === 'saved' && r.inviteId) {
          await api.patch(`/teams/${teamId}/invite/${r.inviteId}`, { classification: entry.cls });
          ok++;
        }
      } catch { fail++; }
    }
    if (ok)   toast.success(`${ok} player${ok !== 1 ? 's' : ''} reclassified`);
    if (fail) toast.error(`${fail} failed to update`);
    setReclassify(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, { ...v, changed: false }])));
    setSaving(false);
  };

  const sendInvites = async () => {
    if (!pendingInviteIds.length) return;
    setSendingInvites(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/import-players/send-invites`, { inviteIds: pendingInviteIds });
      toast.success(`📧 ${data.sent} invite${data.sent !== 1 ? 's' : ''} sent!`);
      if (data.failed > 0) toast.error(`${data.failed} failed to send`);
      setInvitesSent(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invites');
    }
    setSendingInvites(false);
  };

  const statCard = (icon, count, label, color) => (
    <div style={{ flex: 1, minWidth: 80, padding: '14px 12px', borderRadius: 12, textAlign: 'center', background: `${color}10`, border: `1px solid ${color}30` }}>
      <div style={{ color, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: "'Barlow Condensed',sans-serif" }}>{count}</div>
      <div style={{ fontSize: 10, color: T.text2, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,232,122,0.12)', border: '2px solid rgba(0,232,122,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
          <CheckCircle size={32} style={{ color: T.goal }} />
        </div>
        <h2 style={{ fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", fontSize: 24, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.text, margin: '0 0 5px' }}>Players Saved</h2>
        <p style={{ color: T.text2, fontSize: 14 }}>{[saved > 0 && `${saved} saved`, added > 0 && `${added} added directly`, skipped > 0 && `${skipped} skipped`].filter(Boolean).join(' · ')}</p>
      </div>

      {/* flexWrap: mobile pass — 3 cards each carry minWidth:80, which can
          just barely exceed a ~360-390px viewport once the page/card
          padding is subtracted. Wrap defensively so a tight phone stacks
          instead of clipping; no-op on desktop where they always fit. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {statCard(<UserCheck size={16} />, added,   'Added',   '#00e87a')}
        {statCard(<Mail size={16} />,      saved,   'Saved',   '#00d4ff')}
        {statCard(<UserX size={16} />,     skipped, 'Skipped', '#8a96a8')}
      </div>

      {/* ── Invite notice ── */}
      {pendingInviteIds.length > 0 && !invitesSent && (
        <div style={{
          marginBottom: 20, padding: '14px 16px', borderRadius: 14,
          background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <Mail size={18} style={{ color: T.ice, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>
              {pendingInviteIds.length} player{pendingInviteIds.length !== 1 ? 's' : ''} saved — no invite sent yet
            </p>
            <p style={{ fontSize: 12, color: T.text2, margin: '2px 0 0' }}>
              They won't receive anything until you send invites. You can also do this later from the Players page.
            </p>
          </div>
          <button
            onClick={sendInvites}
            disabled={sendingInvites}
            title="Sends each saved player an email invite to create a Game Day account and join the team. They'll use the link in the email to sign up."
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700,
              background: T.ice, color: '#000', border: 'none', cursor: 'pointer', flexShrink: 0,
              fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase',
            }}
          >
            {sendingInvites
              ? <><div style={{ width: 13, height: 13, border: '2px solid rgba(0,0,0,0.3)', borderTopColor: '#000', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Sending…</>
              : <><Mail size={13} /> Send Initial Invites</>}
          </button>
        </div>
      )}

      {/* Invites sent confirmation */}
      {invitesSent && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 12,
          background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.2)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <CheckCircle size={16} style={{ color: T.goal, flexShrink: 0 }} />
          <p style={{ fontSize: 13, color: T.text, margin: 0 }}>
            Invites sent! Players will receive an email to join the team.
          </p>
        </div>
      )}

      {/* ── Post-import reclassify panel ── */}
      {reclassifiable.length > 0 && (
        <div style={{ marginBottom: 20, padding: '16px 18px', borderRadius: 14, background: T.s1, border: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Review &amp; Reclassify Imported Players</p>
              <p style={{ fontSize: 12, color: T.text2, margin: '2px 0 0' }}>Change any player's classification. Updates apply when they join.</p>
            </div>
            {changes.length > 0 && (
              <button onClick={saveReclassify} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', flexShrink: 0, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {saving ? '…' : `Save ${changes.length} Change${changes.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>

          {/* Mobile pass — same fixed-px-column clipping risk as the
              Review-step table above (120px + 90px fixed columns leave
              almost nothing for the 1fr name/email column once the
              page/card/panel padding is subtracted at ~390px). Wrap in
              overflow-x:auto + minWidth so it scrolls instead of
              squeezing the select/status columns unreadable. No-op on
              desktop, which already has room. */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}`, minWidth: 380 }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px', background: T.s2, borderBottom: `1px solid ${T.border}`, padding: '7px 12px' }}>
              {['Player', 'Classification', 'Status'].map((h, i) => (
                <div key={i} style={{ fontSize: 10, fontWeight: 700, color: T.text2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>

            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {reclassifiable.map((r, i) => {
                const entry = reclassify[r.email];
                const isChanged = entry?.changed;
                return (
                  <div key={r.email} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px', padding: '9px 12px', borderBottom: i < reclassifiable.length - 1 ? `1px solid ${T.border}` : 'none', alignItems: 'center', background: isChanged ? `${T.ice}08` : 'transparent' }}>
                    <div style={{ minWidth: 0 }}>
                      {r.name && <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>}
                      <div style={{ fontSize: 11, color: T.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                    </div>
                    <select
                      value={entry?.cls || 'player_full'}
                      onChange={e => setReclassify(prev => ({ ...prev, [r.email]: { cls: e.target.value, changed: e.target.value !== (r.classification || 'player_full') } }))}
                      style={{ background: T.s2, border: `1px solid ${isChanged ? T.ice : T.border}`, borderRadius: 7, color: T.text, fontSize: 11, padding: '4px 6px', outline: 'none', fontFamily: 'inherit' }}
                    >
                      {CLASSES_LIST.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
                    </select>
                    <span style={{ fontSize: 11, fontWeight: 700, color: r.status === 'saved' ? T.ice : T.goal, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {r.status === 'saved' ? '📋 Saved' : '✓ Added'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Full results list */}
      {results.filter(r => r.status === 'skipped' || r.status === 'error').length > 0 && (
        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
          {results.filter(r => r.status === 'skipped' || r.status === 'error').map((r, i, arr) => (
            <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}>
              <X size={13} style={{ color: r.status === 'error' ? T.penalty : T.text2, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {r.name && <span style={{ fontSize: 13, color: T.text, fontWeight: 600, marginRight: 6 }}>{r.name}</span>}
                <span style={{ fontSize: 12, color: T.text2 }}>{r.email}</span>
              </div>
              <span style={{ fontSize: 11, color: r.status === 'error' ? T.penalty : T.text2 }}>{r.reason || r.status}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onImportMore} style={{ flex: 1, padding: '11px 0', borderRadius: 11, fontSize: 13, fontWeight: 600, background: T.s2, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer' }}>Import More</button>
        <button onClick={onGoToRoster} style={{ flex: 2, padding: '11px 0', borderRadius: 11, fontSize: 14, fontWeight: 700, background: T.ice, border: 'none', color: '#000', cursor: 'pointer', fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', boxShadow: `0 4px 20px ${T.iceBdr}` }}>View Players</button>
      </div>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderRadius: 12, marginBottom: 20, background: 'rgba(255,59,59,0.10)', border: '1.5px solid rgba(255,59,59,0.35)' }}>
      <AlertTriangle size={18} style={{ color: '#ff3b3b', flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <p style={{ color: T.text, fontSize: 14, fontWeight: 700, margin: '0 0 3px' }}>Import Failed</p>
        <p style={{ color: T.text, fontSize: 13, margin: 0, opacity: 0.85, wordBreak: 'break-word' }}>{message}</p>
      </div>
      {onDismiss && <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: T.text2, cursor: 'pointer', padding: 2 }}><X size={15} /></button>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ImportPlayersPage() {
  const { teamId } = useParams();
  const navigate   = useNavigate();
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [step,         setStep]        = useState(0);
  const [preset,       setPreset]      = useState('player_full'); // default classification
  const [imageQueue,   setImageQueue]  = useState([]);
  const [extracted,    setExtracted]   = useState(null);
  const [importResult, setImportResult]= useState(null);
  const [error,        setError]       = useState(null);

  if (!roleLoading && !isCoach) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <AlertTriangle size={48} style={{ color: T.penalty, margin: '0 auto 16px' }} />
        <h2 style={{ color: T.text }}>Coaches Only</h2>
        <p style={{ color: T.text2 }}>Player import is only available to coaches and managers.</p>
        <button onClick={() => navigate(`/team/${teamId}/players`)} style={{ marginTop: 20, padding: '10px 24px', background: T.ice, color: '#000', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>Back to Players</button>
      </div>
    );
  }

  const handleAddImages = entries => {
    setImageQueue(prev => {
      const rem = MAX_IMAGES - prev.length;
      if (entries.length > rem) toast.error(`Only ${rem} more screenshot${rem !== 1 ? 's' : ''} can be added (max ${MAX_IMAGES}).`);
      const toAdd = entries.slice(0, rem);
      if (toAdd.length) toast.success(`${toAdd.length} screenshot${toAdd.length !== 1 ? 's' : ''} added`);
      return [...prev, ...toAdd];
    });
    setError(null);
  };

  const handleRemoveImage = id => setImageQueue(prev => prev.filter(f => f.id !== id));

  const handleSpreadsheet = ({ players, fileInfo: fi }) => {
    setImageQueue([]);
    setError(null);
    if (!players.length) { setError('No players found. Make sure the spreadsheet has Name/Email columns.'); return; }
    setExtracted({ players: players.map(p => ({ ...p, classification: preset || 'player_full' })), provider: null, filename: fi.name });
    setStep(2);
    toast.success(`📊 Found ${players.length} player${players.length !== 1 ? 's' : ''} — review below`);
  };

  const handleAnalyze = async () => {
    if (!imageQueue.length) { toast.error('Add at least one screenshot first'); return; }
    setStep(1);
    setError(null);
    const allPlayers = [];
    let provider = null, errorCount = 0;

    for (const file of imageQueue) {
      setImageQueue(prev => prev.map(f => f.id === file.id ? { ...f, status: 'analyzing' } : f));
      try {
        const { data } = await api.post(`/teams/${teamId}/import-players/extract`, { filename: file.name, mimeType: file.mimeType, data: file.data, fileSize: file.size }, { timeout: 90_000 });
        allPlayers.push(...data.players.map(p => ({ ...p, classification: preset || 'player_full' })));
        if (data.provider) provider = data.provider;
        setImageQueue(prev => prev.map(f => f.id === file.id ? { ...f, status: 'done', count: data.players.length } : f));
      } catch (err) {
        errorCount++;
        setImageQueue(prev => prev.map(f => f.id === file.id ? { ...f, status: 'error', error: err.response?.data?.error || err.message } : f));
      }
    }

    if (!allPlayers.length) { setError('No player details extracted. Try clearer images or CSV/Excel.'); setStep(0); return; }
    const ok = imageQueue.length - errorCount;
    setExtracted({ players: allPlayers, provider, filename: imageQueue.length === 1 ? imageQueue[0].name : `${ok} screenshot${ok !== 1 ? 's' : ''}` });
    setStep(2);
  };

  const handleConfirm = async players => {
    try {
      const { data } = await api.post(`/teams/${teamId}/import-players/confirm`, { players });
      setImportResult(data);
      setStep(3);
      const total = (data.saved || 0) + (data.added || 0);
      toast.success(`🏒 ${total} player${total !== 1 ? 's' : ''} imported!`);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Import failed. Please try again.');
      throw err;
    }
  };

  const handleReset = () => { setStep(0); setImageQueue([]); setExtracted(null); setError(null); setImportResult(null); };

  return (
    <div style={{ padding: '32px 24px', maxWidth: 840, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 40 }}>
        <button onClick={() => navigate(`/team/${teamId}/players`)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600, background: T.s1, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer', flexShrink: 0, marginTop: 4 }}>
          <ArrowLeft size={14} /> Roster
        </button>
        <div>
          <h1 style={{ fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", fontSize: 34, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: T.text, margin: 0, lineHeight: 1 }}>
            <span style={{ color: T.ice }}>Import</span> Players
          </h1>
          <p style={{ color: T.text2, fontSize: 14, marginTop: 5 }}>
            Upload up to {MAX_IMAGES} contact screenshots — AI extracts name, email &amp; phone from each.
          </p>
        </div>
      </div>

      <StepBar step={step} />
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      <div style={{ background: T.s1, border: `1px solid ${T.border}`, borderRadius: 20, padding: 28 }}>
        {step === 0 && (
          <>
            <UploadStep imageQueue={imageQueue} preset={preset} onPresetChange={setPreset} onAddImages={handleAddImages} onRemoveImage={handleRemoveImage} onSpreadsheet={handleSpreadsheet} />
            {imageQueue.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <button onClick={handleAnalyze} style={{ width: '100%', padding: '13px 0', borderRadius: 13, fontSize: 15, fontWeight: 700, background: T.ice, color: '#000', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontFamily: "'Barlow Condensed','Arial Narrow',Arial,sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', boxShadow: `0 4px 24px ${T.iceBdr}`, transition: 'all 0.2s' }}>
                  <Zap size={17} /> Analyze {imageQueue.length} Screenshot{imageQueue.length !== 1 ? 's' : ''} with AI <ChevronRight size={17} />
                </button>
              </div>
            )}
          </>
        )}
        {step === 1 && <AnalyzingStep imageQueue={imageQueue} />}
        {step === 2 && extracted && <ReviewStep players={extracted.players} provider={extracted.provider} filename={extracted.filename} preset={preset} onConfirm={handleConfirm} onReset={handleReset} />}
        {step === 3 && importResult && <DoneStep result={importResult} teamId={teamId} onGoToRoster={() => navigate(`/team/${teamId}/players`)} onImportMore={handleReset} />}
      </div>

      {step < 2 && <p style={{ color: T.border, fontSize: 12, textAlign: 'center', marginTop: 16 }}>Images sent to AI only — never stored on our servers. Spreadsheets processed locally.</p>}
    </div>
  );
}
